import { useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Filter, Package, Plus, Search, Upload } from 'lucide-react'
import { toast } from 'sonner'

import type { ProductImportResult } from '@/lib/api/product-api'
import type {
    ProductActiveFilter,
    ProductsSearch,
} from '@/lib/products-list-search'

import {
    parseProductsSearch,
    resolveProductActiveFilter,
    toIsActiveParam,
} from '@/lib/products-list-search'
import {
    LIST_SEARCH_KEYS,
    useRememberListSearch,
} from '@/lib/list-search-store'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { ProductImportResultDialog } from '@/components/products/product-import-result-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useImportProducts, useProducts } from '@/hooks/use-product-query'
import {
    useInventoryByWarehouse,
    useWarehouses,
} from '@/hooks/use-inventory-query'
import { useAuth } from '@/hooks/use-auth'
import { useDebounced } from '@/hooks/use-debounced'
import { formatQty, formatVND } from '@/lib/format'
import { getProductImageURL } from '@/lib/product-image'
import { m } from '@/lib/paraglide/messages'

export type { ProductsSearch }

export const Route = createFileRoute('/_app/_authed/_sidebar/products/')({
    component: ProductsPage,
    validateSearch: parseProductsSearch,
})

function ProductsPage() {
    const navigate = useNavigate({ from: '/products/' })
    const urlSearch = Route.useSearch()
    useRememberListSearch(LIST_SEARCH_KEYS.products, urlSearch)
    const setSearch = (
        updater: (prev: ProductsSearch) => ProductsSearch,
        opts?: { replace?: boolean },
    ) => void navigate({ search: updater, replace: opts?.replace ?? false })

    // Ô tìm kiếm giữ state cục bộ để gõ mượt, debounce rồi mới đẩy lên URL —
    // tránh spam history mỗi ký tự.
    const [searchInput, setSearchInput] = useState(urlSearch.q ?? '')
    const debouncedSearch = useDebounced(searchInput, 300)
    useEffect(() => {
        if (debouncedSearch === (urlSearch.q ?? '')) return
        setSearch(
            (prev) => ({
                ...prev,
                q: debouncedSearch || undefined,
                page: undefined,
            }),
            { replace: true },
        )
    }, [debouncedSearch])
    // URL đổi từ ngoài (back/forward, deep-link) → đồng bộ lại ô input.
    useEffect(() => {
        setSearchInput(urlSearch.q ?? '')
    }, [urlSearch.q])

    // Mặc định lọc "đang hoạt động" ngay khi vào màn, chọn "tất cả" mới bỏ lọc.
    const activeFilter = resolveProductActiveFilter(urlSearch.active)
    const sellableFilter = urlSearch.sellable ?? 'all'
    const page = urlSearch.page ?? 1

    const [importResult, setImportResult] =
        useState<ProductImportResult | null>(null)
    const [importResultOpen, setImportResultOpen] = useState(false)
    const [filterDialogOpen, setFilterDialogOpen] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const importMutation = useImportProducts()
    const { hasPermission } = useAuth()
    // Warehouse staff get read-only access (products.read) to look up products
    // while receiving stock; create/import/edit stay with product managers.
    const canCreate = hasPermission('products.create')
    const canManage = hasPermission('products.update')

    // Listen for SSE product import result events dispatched by useSSENotifications
    useEffect(() => {
        const handler = (e: Event) => {
            const result = (e as CustomEvent<ProductImportResult>).detail
            setImportResult(result)
            setImportResultOpen(true)
        }
        window.addEventListener('product-import-result', handler)
        return () =>
            window.removeEventListener('product-import-result', handler)
    }, [])

    const { data, isLoading } = useProducts({
        search: urlSearch.q || undefined,
        is_active: toIsActiveParam(activeFilter),
        page,
        per_page: 20,
    })

    const allProducts = data?.data ?? []
    const products =
        sellableFilter === 'sellable'
            ? allProducts.filter((p) => p.is_sellable !== false)
            : sellableFilter === 'internal'
              ? allProducts.filter((p) => p.is_sellable === false)
              : allProducts

    // Tồn theo warehouse mặc định để show 1 cột "Số lượng tồn" cạnh các cột giá.
    const { data: warehouses = [] } = useWarehouses()
    const defaultWarehouseId =
        warehouses.find((w) => w.is_default)?.id ?? warehouses[0]?.id
    const { data: inventoryData = [] } =
        useInventoryByWarehouse(defaultWarehouseId)
    const stockMap = inventoryData.reduce<Record<string, number>>(
        (acc, row) => {
            acc[row.product_id] = (acc[row.product_id] ?? 0) + row.quantity
            return acc
        },
        {},
    )
    const meta = data?.meta
    const renderFilters = () => (
        <>
            <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    placeholder={m.product_search_placeholder()}
                    className="pl-9"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
            </div>
            <Select
                value={activeFilter}
                onValueChange={(v) =>
                    setSearch((prev) => ({
                        ...prev,
                        // Ghi cả 'all' vào URL vì thiếu param nghĩa là "đang hoạt động".
                        active: v as ProductActiveFilter,
                        page: undefined,
                    }))
                }
            >
                <SelectTrigger className="w-full md:w-44">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">
                        {m.vehicle_status_all()}
                    </SelectItem>
                    <SelectItem value="active">{m.product_active()}</SelectItem>
                    <SelectItem value="inactive">
                        {m.product_inactive_label()}
                    </SelectItem>
                </SelectContent>
            </Select>
            <Select
                value={sellableFilter}
                onValueChange={(v) =>
                    setSearch((prev) => ({
                        ...prev,
                        sellable:
                            v === 'all'
                                ? undefined
                                : (v as 'sellable' | 'internal'),
                        page: undefined,
                    }))
                }
            >
                <SelectTrigger className="w-full md:w-44">
                    <SelectValue
                        placeholder={m.product_filter_sellable_placeholder()}
                    />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">
                        {m.product_filter_all_sellable()}
                    </SelectItem>
                    <SelectItem value="sellable">
                        {m.product_filter_sellable()}
                    </SelectItem>
                    <SelectItem value="internal">
                        {m.product_filter_internal()}
                    </SelectItem>
                </SelectContent>
            </Select>
        </>
    )

    return (
        <div className="space-y-4 p-4 md:p-6">
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    importMutation.mutate(file, {
                        onSuccess: () => {
                            toast.info(m.import_products_processing())
                        },
                        onError: () => toast.error(m.import_upload_failed()),
                    })
                    e.target.value = ''
                }}
            />
            <ListPageHeader
                icon={<Package className="size-5 shrink-0" />}
                title={m.product_title()}
                utilityActions={
                    <Button
                        variant="outline"
                        size="icon"
                        className="md:hidden"
                        onClick={() => setFilterDialogOpen(true)}
                    >
                        <Filter className="size-4" />
                    </Button>
                }
                primaryActions={
                    canCreate ? (
                        <>
                            <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={importMutation.isPending}
                            >
                                <Upload className="size-4" />
                                {importMutation.isPending
                                    ? m.import_importing()
                                    : m.import_excel()}
                            </Button>
                            <Button
                                size="sm"
                                className="w-full"
                                onClick={() =>
                                    navigate({ to: '/products/new' })
                                }
                            >
                                <Plus className="size-4" />
                                {m.product_add_new()}
                            </Button>
                        </>
                    ) : null
                }
            />

            <div className="hidden md:flex md:flex-wrap gap-3">
                {renderFilters()}
            </div>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogContent className="max-w-none w-screen h-[100svh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6">
                    <DialogHeader>
                        <DialogTitle>{m.product_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="grid content-start gap-3 overflow-y-auto py-2">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            className="h-11 flex-1"
                            onClick={() => setFilterDialogOpen(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-14">
                                {m.product_image_header()}
                            </TableHead>
                            <TableHead>{m.product_sku()}</TableHead>
                            <TableHead>{m.product_barcode()}</TableHead>
                            <TableHead>{m.product_name()}</TableHead>
                            <TableHead>{m.product_unit()}</TableHead>
                            <TableHead>{m.product_cost_price()}</TableHead>
                            <TableHead>{m.product_unit_price()}</TableHead>
                            <TableHead>
                                {m.product_stock_qty_header()}
                            </TableHead>
                            <TableHead>{m.product_status()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : products.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.product_not_found()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            products.map((p) => {
                                const primaryImg =
                                    p.images?.find((i) => i.is_primary) ??
                                    p.images?.[0]
                                return (
                                    <TableRow
                                        key={p.id}
                                        className={
                                            canManage
                                                ? 'cursor-pointer hover:bg-muted/50'
                                                : undefined
                                        }
                                        onClick={
                                            canManage
                                                ? () =>
                                                      navigate({
                                                          to: '/products/$productId',
                                                          params: {
                                                              productId: p.id,
                                                          },
                                                      })
                                                : undefined
                                        }
                                    >
                                        <TableCell>
                                            {primaryImg ? (
                                                <img
                                                    src={getProductImageURL(
                                                        primaryImg.image_url,
                                                    )}
                                                    alt={p.name}
                                                    className="size-10 rounded object-cover bg-muted"
                                                />
                                            ) : (
                                                <div className="size-10 rounded bg-muted flex items-center justify-center">
                                                    <Package className="size-5 text-muted-foreground" />
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">
                                            {p.sku}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {p.barcode ?? '—'}
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {p.name}
                                        </TableCell>
                                        <TableCell>{p.unit}</TableCell>
                                        <TableCell>
                                            {formatVND(p.cost_price)} ₫
                                        </TableCell>
                                        <TableCell>
                                            {formatVND(p.unit_price)} ₫
                                        </TableCell>
                                        <TableCell>
                                            {typeof stockMap[p.id] === 'number'
                                                ? `${formatQty(stockMap[p.id])} ${p.unit}`
                                                : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1 items-start">
                                                <Badge
                                                    variant={
                                                        p.is_active
                                                            ? 'success'
                                                            : 'secondary'
                                                    }
                                                >
                                                    {p.is_active
                                                        ? m.customer_active()
                                                        : m.customer_inactive()}
                                                </Badge>
                                                {p.is_sellable === false ? (
                                                    <Badge
                                                        variant="secondary"
                                                        className="bg-orange-100 text-orange-700"
                                                    >
                                                        {m.product_internal_badge()}
                                                    </Badge>
                                                ) : null}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.product_total_count({ count: String(meta.total) })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() =>
                                setSearch((prev) => ({
                                    ...prev,
                                    page: page - 1 > 1 ? page - 1 : undefined,
                                }))
                            }
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() =>
                                setSearch((prev) => ({
                                    ...prev,
                                    page: page + 1,
                                }))
                            }
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                </div>
            )}

            <ProductImportResultDialog
                result={importResult}
                open={importResultOpen}
                onOpenChange={setImportResultOpen}
            />
        </div>
    )
}
